import { TelegramAltIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { RadioButton } from "@/components/RadioButton"
import { NIL_UUID } from "@/domain/resources/constants"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { getSharedEntityType } from "@/domain/sharedData/getSharedUtils"
import { useControlledRadioProps } from "@/hooks/useControlledRadioProps"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type PatchInterbuBodyOne,
  type PatchInterbuBodyTwo,
  type SharedDataJsonldReadCollectionSharedData,
  getGetUserSharedDataQueryKey,
  getGetUserSharedDatasQueryKey,
  usePatchInterbu,
} from "@/networking/api"
import { excludePropertiesOf } from "@/utils/ts-utils"
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalFooter,
  Textarea,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { AttachmentInput } from "../AttachmentInput"
import FormattedErrorMessage from "../FormattedErrorMessage/FormattedErrorMessage"
import { RequiredErrorMessage } from "../RequiredErrorMessage"
import SharedEntityName from "./SharedEntityName"
import { SHARING_ABORT_MOTIVATION } from "./types"

type FormType = Partial<PatchInterbuBodyOne> & Pick<PatchInterbuBodyTwo, "attachment">

type Props = {
  isOpen: boolean
  onClose: () => void
  sharedEntity: SharedDataJsonldReadCollectionSharedData
  isKo: boolean
}

export default function ShareEndModal({ isOpen, onClose, sharedEntity, isKo }: Props) {
  const entityType = getSharedEntityType(sharedEntity)

  const defaultValues: FormType = {
    message_end: "",
    subject_end: (isKo ? SHARING_ABORT_MOTIVATION.ABORTED_BY_CUSTOMER : "") as string,
    attachment: undefined,
  }

  const modalForm = useForm<FormType>({
    defaultValues,
  })
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = modalForm

  const accessAbortMotivationOptions = [
    {
      value: SHARING_ABORT_MOTIVATION.ABORTED_BY_CUSTOMER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.ABORTED_BY_CUSTOMER}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.DONE_BY_OTHER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.DONE_BY_OTHER}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.PROJECT_NOT_POSSIBLE,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.PROJECT_NOT_POSSIBLE}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.NO_ARRANGEMENT_FOUND,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.NO_ARRANGEMENT_FOUND}`,
    },
    {
      value: SHARING_ABORT_MOTIVATION.OTHER,
      translationId: `userSharedData.${entityType}.motivation.${SHARING_ABORT_MOTIVATION.OTHER}`,
    },
  ]

  const { getRadioProps: getMotivationRadioProps } = useControlledRadioProps({
    control,
    name: "subject_end",
    defaultValue: defaultValues.subject_end,
    rules: { required: isKo },
  })

  const { mutate, isPending } = usePatchInterbu({
    request: {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  })
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()

  const postShareRequest = (formData: FormType) => {
    const json_data: Partial<PatchInterbuBodyOne> = excludePropertiesOf(formData, ["attachment"])
    json_data.shareddata = {
      id: sharedEntity.id,
      status: isKo ? SHARED_DATA_STATUS.CLOSED : SHARED_DATA_STATUS.COMPLETED,
    }
    const attachment = formData.attachment instanceof Blob ? formData.attachment : undefined

    mutate(
      {
        id: sharedEntity.interBu?.id ?? NIL_UUID,
        data: {
          attachment: attachment,
          json_data: JSON.stringify(json_data),
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDataQueryKey(sharedEntity.id),
          })
          void queryClient.invalidateQueries({
            queryKey: getGetUserSharedDatasQueryKey(),
          })
          onClose()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id={`userSharedData.${isKo ? "end_ko" : "end_ok"}`} />}
      scrollBehavior="outside"
    >
      <form onSubmit={handleSubmit(postShareRequest)}>
        <ModalBody>
          <Flex flexDir="column" gap="6">
            <Box>
              <FormattedMessage id={`userSharedData.${isKo ? "end_ko" : "end_ok"}.message`} />
            </Box>

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.object" />
              </FormLabel>
              <SharedEntityName item={sharedEntity} displayInputLike={true} />
            </Box>

            {isKo ? (
              <Box>
                <FormLabel>
                  <FormattedMessage id="userSharedData.motivation" />
                </FormLabel>
                <FormControl isInvalid={!!errors.subject_end}>
                  <Flex justify="space-between">
                    {accessAbortMotivationOptions.map(subject => {
                      return (
                        <RadioButton
                          key={subject.value}
                          labelTranslationId={subject.translationId}
                          {...getMotivationRadioProps({ value: subject.value })}
                        />
                      )
                    })}
                  </Flex>
                  <RequiredErrorMessage />
                </FormControl>
              </Box>
            ) : (
              <Box>
                <FormLabel>
                  <FormattedMessage id="userSharedData.result_doc" />
                </FormLabel>
                <FormControl as="div" isInvalid={!!errors.attachment}>
                  <AttachmentInput
                    {...register("attachment")}
                    modalForm={modalForm}
                    accept="image/*,.pdf"
                    borderColor="line"
                  />
                  <FormattedErrorMessage fieldError={errors.attachment} />
                </FormControl>
              </Box>
            )}

            <Box>
              <FormLabel>
                <FormattedMessage id="userSharedData.comment" />
              </FormLabel>
              <Textarea {...register("message_end")} variant="outlineWithBorder" />
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button display="flex" gap="2" type="submit" isLoading={isPending}>
            <FormattedMessage id="confirm" />
            <TelegramAltIcon boxSize={4} />
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
